import { createSelector } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import companyToken from "Utils/companyToken";
import { prepareHeadersWithCSRFToken } from "Utils/fetchWithCSRFToken";
import { convertDataToFormDataFormat } from "Utils/formDataHelpers";

import { appendAuthParams } from "Common/TokenAuth/Utils";

interface UploadFileArgs {
  carrier: string;
  file: File[];
}

export const borApplicationApi = createApi({
  reducerPath: "borApplicationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    getApplication: builder.query({
      query: () => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`v1/broker_of_record/company_profile?${params}`);
      },
      transformResponse: (response, _meta, _arg) => {
        return {
          ...response,
          // Currently the BOR flow doesn't support Tiered Contributions,
          // so let's set some default values here.
          companyContributions: formatContributions(response),
          contributionMethod: "non_tiered",
          typeOfContribution: response.typeOfContribution || "percentage",
        };
      },
      providesTags: ["Application"],
    }),

    updateApplication: builder.mutation({
      query: (body) => ({
        url: appendAuthParams("v1/broker_of_record/update_company_profile"),
        method: "POST",
        body: {
          ...body,
          company_id_token: companyToken(),
        },
      }),

      invalidatesTags: ["Application"],
    }),

    uploadPlanDocumentsFile: builder.mutation<{ success: true }, UploadFileArgs>({
      query: ({ carrier, file }) => {
        const companyIDToken = companyToken();
        return {
          url: appendAuthParams(`v1/broker_of_record/upload_plan_documents`),
          method: "PUT",
          body: convertDataToFormDataFormat({
            carrier,
            file,
            ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
          }),
        };
      },

      invalidatesTags: ["Application"],
    }),

    getNetsuiteDeductionAccounts: builder.query({
      query: () => appendAuthParams(`v2/partner/netsuite-payroll-account-items`),
    }),

    updateNetsuitedDeductionAccounts: builder.mutation({
      query: (preferences) => ({
        url: appendAuthParams("partner/preferences"),
        method: "PUT",
        body: {
          preferences,
          company_id_token: companyToken(),
        },
      }),
    }),

    saveSignature: builder.mutation({
      query: (body) => ({
        url: appendAuthParams("v1/broker_of_record/save_signature"),
        method: "POST",
        body: {
          ...body,
          company_id_token: companyToken(),
        },
      }),

      invalidatesTags: ["Application"],
    }),

    getAccount: builder.query({
      query: () => appendAuthParams(`v1/broker_of_record/account`),
      providesTags: ["Account"],
    }),

    updateAccount: builder.mutation({
      query: (body) => ({
        url: appendAuthParams("v1/broker_of_record/update_account"),
        method: "POST",
        body,
      }),

      invalidatesTags: ["Account"],
    }),
  }),

  tagTypes: ["Application", "Account"],
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetApplicationQuery,
  useUpdateApplicationMutation,
  useGetNetsuiteDeductionAccountsQuery,
  useUpdateNetsuitedDeductionAccountsMutation,
  useSaveSignatureMutation,
  useGetAccountQuery,
  useUpdateAccountMutation,
  useUploadPlanDocumentsFileMutation,
} = borApplicationApi;

const selectBorApplicationResult = borApplicationApi.endpoints.getApplication.select();

export const getBorApplication = createSelector(selectBorApplicationResult, (borApplicationResult) => borApplicationResult?.data);

const formatContributions = (borApplication) => {
  const contributions = borApplication?.companyContributions || [];
  const nonTiered = contributions.find((c) => c.role === null);
  if (!nonTiered) {
    return [DEFAULT_NON_TIERED_CONTRIBUTION_VALUES];
  }
  return [
    {
      ...nonTiered,
      active: true,
    },
  ];
};

const DEFAULT_NON_TIERED_CONTRIBUTION_VALUES = {
  employeePercentage: 50,
  dependentPercentage: 0,
  employeeFixedAmount: null,
  dependentFixedAmount: null,
  active: true,
};
